// jwt.ts — base64url-safe JWT payload decoder.
//
// The JWT spec (RFC 7519) uses base64url encoding (RFC 4648 §5): `-` replaces
// `+` and `_` replaces `/`; padding (`=`) is OPTIONAL and most providers omit
// it. `atob` expects standard base64 with `+`/`/` and strict 4-byte padding, so
// calling it directly on a JWT segment is wrong whenever the payload contains
// `-` or `_` chars or the length isn't a multiple of 4 — it throws instead of
// returning the decoded bytes.
//
// This module is the single place in the SPA that touches JWT decoding, so the
// bug cannot resurface elsewhere. The helper is best-effort (it returns `null`
// on any malformed token, e.g. completely forged) — callers should treat a
// `null` result as "unauthenticated" and not crash.

/**
 * Decode the payload section of a JWT (header.payload.sig) and return it as a
 * plain object. Returns `null` if the token is malformed or the payload is not
 * valid JSON.
 *
 * The decode pipeline:
 *   base64url segment → standard base64 (- → +, _ → /) → pad to 4-byte
 *   boundary → atob → UTF-8 bytes via TextDecoder → JSON.parse
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const segment = parts[1];

    // base64url → standard base64
    const b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    // Restore omitted `=` padding (length must be a multiple of 4)
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");

    // atob gives us a byte string; TextDecoder handles multi-byte UTF-8
    // codepoints that would otherwise be corrupted by a naïve charCodeAt loop.
    const binaryStr = atob(padded);
    const bytes = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}
